/**
 * The "How I fly" card (register P2). The details the best fare is worked out
 * from, and the same fields Steward reads. Status is shown, not edited here: it
 * lives on the airline accounts further down this page.
 *
 * Unset stays unset. "Not told" and "no" are different answers: the first keeps
 * Steward's gentle question available, the second closes it.
 */
import { useEffect, useState } from "react";
import { Check, Loader2, Plane, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { howIFlyApi } from "@/lib/api";
import type { HowIFly } from "@/lib/types";

const SEATS: { value: NonNullable<HowIFly["wantsSeat"]>; label: string }[] = [
  { value: "none", label: "I don't pay for a seat" },
  { value: "any", label: "Any seat I choose" },
  { value: "aisle", label: "Aisle" },
  { value: "window", label: "Window" },
  { value: "exit", label: "Exit row or extra legroom" },
];

const selectClass =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]";

/** Ground rows as typed, to the map we store. Blank or partial rows are simply left out. */
export function groundMap(rows: { airport: string; cost: string }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    const code = r.airport.trim().toUpperCase();
    const n = Number(r.cost);
    if (/^[A-Z0-9]{3}$/.test(code) && r.cost.trim() !== "" && Number.isFinite(n) && n >= 0) out[code] = n;
  }
  return out;
}

/** Balance rows as typed, to the map we store. Blank or partial rows are simply left out. */
export function balanceMap(rows: { program: string; miles: string }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    const program = r.program.trim();
    const n = Number(r.miles.replace(/,/g, ""));
    if (program && r.miles.trim() !== "" && Number.isInteger(n) && n >= 0) out[program] = n;
  }
  return out;
}

export default function HowIFlyCard() {
  const [how, setHow] = useState<HowIFly>({});
  const [status, setStatus] = useState<Record<string, string>>({});
  const [ground, setGround] = useState<{ airport: string; cost: string }[]>([]);
  const [balances, setBalances] = useState<{ program: string; miles: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    howIFlyApi
      .get()
      .then((d) => {
        setHow(d.howIFly ?? {});
        setStatus(d.statusByAirline ?? {});
        setGround(Object.entries(d.howIFly?.groundCost ?? {}).map(([airport, cost]) => ({ airport, cost: String(cost) })));
        setBalances(Object.entries(d.howIFly?.milesBalance ?? {}).map(([program, miles]) => ({ program, miles: String(miles) })));
      })
      .catch(() => setSaveState("error"))
      .finally(() => setLoading(false));
  }, []);

  const edit = (patch: Partial<HowIFly>) => {
    setHow((h) => ({ ...h, ...patch }));
    setSaveState("idle");
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const map = groundMap(ground);
      const miles = balanceMap(balances);
      const { groundCost: _old, milesBalance: _oldMiles, ...rest } = how;
      await howIFlyApi.save({
        ...rest,
        ...(Object.keys(map).length > 0 ? { groundCost: map } : {}),
        ...(Object.keys(miles).length > 0 ? { milesBalance: miles } : {}),
      });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  const statuses = Object.entries(status);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plane className="h-5 w-5" aria-hidden="true" />
          How I fly
        </CardTitle>
        <CardDescription>
          We use this to choose your best fare on every trip. Only you can see it. A saved choice on a trip still wins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading" />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="how-seat">Seat</Label>
                <select
                  id="how-seat"
                  className={selectClass}
                  value={how.wantsSeat ?? ""}
                  onChange={(e) => edit({ wantsSeat: (e.target.value || undefined) as HowIFly["wantsSeat"] })}
                >
                  <option value="">Not set</option>
                  {SEATS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="how-bags">Checked bags</Label>
                <select
                  id="how-bags"
                  className={selectClass}
                  value={how.checkedBags ?? ""}
                  onChange={(e) => edit({ checkedBags: e.target.value === "" ? undefined : (Number(e.target.value) as 0 | 1 | 2) })}
                >
                  <option value="">Not set</option>
                  <option value="0">None</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="how-carry">Carry-on</Label>
                <select
                  id="how-carry"
                  className={selectClass}
                  value={how.carryOn === undefined ? "" : how.carryOn ? "yes" : "no"}
                  onChange={(e) => edit({ carryOn: e.target.value === "" ? undefined : e.target.value === "yes" })}
                >
                  <option value="">Not set</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <p className="text-sm font-medium text-foreground">Status</p>
              {statuses.length > 0 ? (
                <p className="text-sm text-muted-foreground">
                  {statuses.map(([code, tier]) => `${code} ${tier}`).join(" · ")}. Change it under Airline Accounts below.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">None yet. Add a tier under Airline Accounts below.</p>
              )}
            </div>

            <div className="space-y-2">
              <div>
                <p className="text-sm font-medium text-foreground">Your own cost to the airport</p>
                <p className="text-xs text-muted-foreground">Optional. What it usually costs you, there and back, by airport. This beats our estimate below.</p>
              </div>
              {ground.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    aria-label="Airport code"
                    placeholder="DFW"
                    maxLength={3}
                    className="w-20 uppercase"
                    value={row.airport}
                    onChange={(e) => {
                      setGround((g) => g.map((r, j) => (j === i ? { ...r, airport: e.target.value } : r)));
                      setSaveState("idle");
                    }}
                  />
                  <span className="text-sm text-muted-foreground" aria-hidden="true">$</span>
                  <Input
                    aria-label="Cost in dollars"
                    inputMode="decimal"
                    placeholder="55"
                    className="w-24"
                    value={row.cost}
                    onChange={(e) => {
                      setGround((g) => g.map((r, j) => (j === i ? { ...r, cost: e.target.value } : r)));
                      setSaveState("idle");
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Remove airport"
                    onClick={() => {
                      setGround((g) => g.filter((_, j) => j !== i));
                      setSaveState("idle");
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setGround((g) => [...g, { airport: "", cost: "" }])}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                Add an airport
              </Button>
            </div>

            <div className="space-y-2">
              <div>
                <p className="text-sm font-medium text-foreground">Miles you hold</p>
                <p className="text-xs text-muted-foreground">Optional. We use this to show your balance after paying with miles on a trip.</p>
              </div>
              {balances.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    aria-label="Miles program"
                    placeholder="AAdvantage"
                    maxLength={40}
                    className="min-w-0 flex-1"
                    value={row.program}
                    onChange={(e) => {
                      setBalances((b) => b.map((r, j) => (j === i ? { ...r, program: e.target.value } : r)));
                      setSaveState("idle");
                    }}
                  />
                  <Input
                    aria-label="Miles balance"
                    inputMode="numeric"
                    placeholder="Miles"
                    className="w-28"
                    value={row.miles}
                    onChange={(e) => {
                      setBalances((b) => b.map((r, j) => (j === i ? { ...r, miles: e.target.value } : r)));
                      setSaveState("idle");
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Remove program"
                    onClick={() => {
                      setBalances((b) => b.filter((_, j) => j !== i));
                      setSaveState("idle");
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setBalances((b) => [...b, { program: "", miles: "" }])}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                Add a program
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <Button size="sm" onClick={save} disabled={saveState === "saving"}>
                {saveState === "saving" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                Save
              </Button>
              <span role="status" className="text-xs text-muted-foreground">
                {saveState === "saved" && "Saved. Your trips use this now."}
                {saveState === "error" && "That did not save. Try again."}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
